<template>
    <div>
        <v-form ref="form" v-on:submit.prevent="submit">
            <!-- Survey Basic Info -->
            <v-card outlined>
                <v-card-text>
                    <v-card outlined>
                        <v-tabs v-model="tab" grow>
                            <v-tab v-for="lang in tenant.languages" :key="'langtab-' + lang.code">
                                <img :src="lang.icon" style="max-height: 20px" />
                                &nbsp;&nbsp;{{ lang.name }}
                                &nbsp;
                                <v-icon v-if="langOk(survey.translations, lang.code, ['title', 'description'])">
                                    {{ icons.mdiCheck }}
                                </v-icon>
                                <v-icon v-else color="error">
                                    {{ icons.mdiBlockHelper }}
                                </v-icon>
                            </v-tab>
                        </v-tabs>

                        <v-tabs-items v-model="tab">
                            <v-tab-item v-for="lang in tenant.languages" :key="'langtabitem-' + lang.code">
                                <div style="padding: 20px">
                                    <img :src="lang.icon" style="max-height: 14px" />
                                    <br />
                                    <br />
                                    <v-text-field hide-details class="mb-4"
                                        v-model="survey.translations[lang.code].title" :label="$t('Otsikko')" outlined
                                        :rules="[required]" />
                                    <quill-editor v-model="survey.translations[lang.code].description"
                                        :options="editorOption" />
                                    <div class="mt-5" v-if="tenant.settings.auto_translations_enabled">
                                        <AutoTranslateButton :current="lang" :languages="tenant.languages"
                                            :is-translating="isTranslating" @click="translate"
                                            translation-errors="translationErrors" :errors="translationErrors">
                                        </AutoTranslateButton>
                                    </div>
                                </div>
                            </v-tab-item>
                        </v-tabs-items>
                    </v-card>

                    <v-card outlined class="mt-5">
                        <v-card-text>
                            <!-- Target Groups -->
                            <GroupSelector v-model="survey.group_ids" :groups="user.admin_groups" />


                            <!-- Form Editor -->
                            <FormEditor :formData="survey.form" :languages="tenant.languages"
                                :hasSubmissions="hasSubmissions" @update:form="updateForm"
                                @editing-state-changed="handleEditingStateChange" />


                            <br />

                            <!-- Actions -->

                            <v-alert v-if="formErrors.length" border="right" colored-border type="error" elevation="2"
                                class="mb-4">
                                <div v-for="error in formErrors" :key="error">{{ error }}</div>
                            </v-alert>

                            <!-- Permission warning or action buttons -->
                            <template v-if="canEdit">
                                <v-btn color="error" v-if="survey.id" @click="deleteSurvey" class="mr-3"
                                    :disabled="isFieldBeingEdited">
                                    {{ $t("Poista") }}
                                </v-btn>
                                <v-btn color="primary" @click="submit" :loading="saving" :disabled="isFieldBeingEdited">
                                    {{ $t("Tallenna") }}
                                </v-btn>
                            </template>

                            <template v-else>
                                <v-alert type="warning" text dense class="mb-4">
                                    {{ $t("Sinulla ei ole oikeuksia kaikkiin tämän julkaisun ryhmiin.") }}
                                    {{ $t("Voit tarkastella julkaisua, mutta et voi tehdä muutoksia.") }}
                                </v-alert>
                            </template>
                        </v-card-text>
                    </v-card>
                </v-card-text>
            </v-card>
        </v-form>
    </div>
</template>

<script>
import { onMounted, reactive, ref, computed } from "@vue/composition-api";
import { useRouter } from "@/@core/utils";
import useAuth from "@/useAuth";
import adminApi from "@/api/admin";
import FormEditor from "@/components/forms/FormEditor.vue";
import GroupSelector from "@/components/admin/GroupSelector.vue";
import { useGroupPermissions } from "@/composables/useGroupPermissions";
import moment from "moment";
import {
    mdiCheck,
    mdiBlockHelper,
    mdiInformation,
} from "@mdi/js";
import { required } from "@/@core/utils/validation";
import { i18n } from "@/plugins/i18n";
import {
    langOk,
    convertTranslationMarkdownToHtml,
    translateObjectTranslations,
} from "@/@core/utils/translation_helpers";
import "quill/dist/quill.core.css";
import "quill/dist/quill.snow.css";
import "quill/dist/quill.bubble.css";
import { quillEditor } from "vue-quill-editor";
import ImageUploader from "quill-image-uploader";
import Quill from "quill";
import "quill-paste-smart";
import AutoTranslateButton from "@/components/AutoTranslateButton.vue";

Quill.register("modules/imageUploader", ImageUploader);

export default {
    components: {
        FormEditor,
        GroupSelector,
        quillEditor,
        AutoTranslateButton,
    },
    props: ["id"],
    setup(props) {
        const { router } = useRouter();
        const { tenant, user } = useAuth();
        const form = ref(null);
        const tab = ref(null);
        const formErrors = ref([]);
        const saving = ref(false);
        const hasSubmissions = ref(false);
        const isFieldBeingEdited = ref(false);
        const isTranslating = ref(false);
        const translationErrors = ref([]);
        const translationCompleted = ref(false);
        const forceUpdateTranslationsKey = ref(new Date().toString());

        const survey = reactive({
            id: null,
            translations: {},
            group_ids: [],
            push_sent: false,
            form: {
                id: null,
                translations: {},
                anonymous: true,
                allow_multiple_submissions: false,
                release_date: "",
                expire_date: "",
                fields: [],
            },
        });

        // Initialize translations
        tenant.value.languages.forEach((lang) => {
            survey.translations[lang.code] = {
                title: "",
                description: "",
            };
            survey.form.translations[lang.code] = {
                title: "",
                description: "",
            };
        });

        // Quill editor configuration
        const editorOption = {
            placeholder: i18n.t("Kuvaus..."),
            modules: {
                clipboard: {
                    keepSelection: true,
                    substituteBlockElements: false,
                    magicPasteLinks: true,
                },
                toolbar: [
                    ["bold", "italic"],
                    ["link", "image"],
                ],
                imageUploader: {
                    upload: (file) => {
                        return new Promise(async (resolve, reject) => {
                            const res = await adminApi.uploadImage(file);
                            if (res.ok) {
                                resolve(res.data);
                            } else {
                                reject("Image upload failed");
                            }
                        });
                    },
                },
            },
        };

        const hasTranslation = (translations, langCode) => {
            return translations[langCode] && translations[langCode].title && translations[langCode].title.trim() !== "";
        };

        const updateForm = (formData) => {
            survey.form = formData;
        };

        const handleEditingStateChange = (isEditing) => {
            isFieldBeingEdited.value = isEditing;
        };

        // Check permissions for editing survey
        const selectedGroupIds = computed(() => survey.group_ids);
        const { canEdit } = useGroupPermissions(selectedGroupIds);

        const getFieldIdentifier = (field, index) => {
            // Try to get a field label from any language
            for (const lang of tenant.value.languages) {
                if (field.translations[lang.code] && field.translations[lang.code].label) {
                    return `"${field.translations[lang.code].label}"`;
                }
            }
            // Fallback to field number if no label found
            return i18n.t("Kentän") + " " + (index + 1);
        };

        const validateForm = () => {
            formErrors.value = [];

            // Check that at least one translation is filled
            let hasAtLeastOneTranslation = false;
            tenant.value.languages.forEach((lang) => {
                if (hasTranslation(survey.translations, lang.code)) {
                    hasAtLeastOneTranslation = true;
                }
            });

            if (!hasAtLeastOneTranslation) {
                formErrors.value.push(i18n.t("Vähintään yhden kielen otsikko on pakollinen"));
            }

            // Check that at least one field exists
            if (survey.form.fields.length === 0) {
                formErrors.value.push(i18n.t("Kyselyssä tulee olla vähintään yksi kenttä"));
            }

            // Validate each field
            survey.form.fields.forEach((field, index) => {
                const fieldIdentifier = getFieldIdentifier(field, index);
                let hasFieldLabel = false;
                tenant.value.languages.forEach((lang) => {
                    if (field.translations[lang.code] && field.translations[lang.code].label) {
                        hasFieldLabel = true;
                    }
                });

                if (!hasFieldLabel) {
                    formErrors.value.push(`${fieldIdentifier} ${i18n.t("otsikko puuttuu kaikilla kielillä")}`);
                }

                // Check options for radio/checkbox fields
                if (field.field_type === 3 || field.field_type === 4) {
                    if (!field.options || field.options.length === 0) {
                        formErrors.value.push(`${fieldIdentifier} ${i18n.t("tulee olla vähintään yksi vastausvaihtoehto")}`);
                    } else {
                        field.options.forEach((option, optIndex) => {
                            let hasOptionLabel = false;
                            tenant.value.languages.forEach((lang) => {
                                if (option.translations[lang.code] && option.translations[lang.code].label) {
                                    hasOptionLabel = true;
                                }
                            });
                            if (!hasOptionLabel) {
                                formErrors.value.push(`${fieldIdentifier} ${i18n.t("vaihtoehto")} ${optIndex + 1} ${i18n.t("puuttuu kaikilla kielillä")}`);
                            }
                        });
                    }
                }
            });

            return formErrors.value.length === 0;
        };

        const submit = async () => {
            if (!form.value.validate()) {
                formErrors.value.push(i18n.t("Tarkista lomakkeen tiedot"));
                return;
            }

            if (!validateForm()) {
                return;
            }

            saving.value = true;

            const response = await adminApi.saveSurvey(survey);

            if (response.ok) {
                router.push({ name: "admin-survey-list" });
            } else {
                formErrors.value = [response.error || i18n.t("Tallentaminen epäonnistui")];
            }

            saving.value = false;
        };

        const deleteSurvey = async () => {
            if (confirm(i18n.t("Haluatko varmasti poistaa kyselyn ja kaikki sen vastaukset?"))) {
                const response = await adminApi.deleteSurvey(survey.id);
                if (response.ok) {
                    router.replace({ name: "admin-survey-list" });
                } else {
                    formErrors.value = [response.error || i18n.t("Poistaminen epäonnistui")];
                }
            }
        };

        const fetchSurvey = async (surveyId) => {
            const response = await adminApi.getSurvey(surveyId);

            if (response.ok) {
                const data = response.data;

                survey.id = data.id;
                survey.group_ids = data.group_ids || [];
                survey.push_sent = data.push_sent || false;

                // Populate translations
                tenant.value.languages.forEach((lang) => {
                    if (data.translations[lang.code]) {
                        survey.translations[lang.code] = data.translations[lang.code];
                    }
                });

                // Convert markdown to HTML for Quill editor
                convertTranslationMarkdownToHtml(survey.translations, ["description"]);

                // Populate form data
                if (data.form) {
                    survey.form.id = data.form.id;
                    survey.form.anonymous = data.form.anonymous;
                    survey.form.allow_multiple_submissions = data.form.allow_multiple_submissions;

                    if (data.form.release_date) {
                        survey.form.release_date = moment(data.form.release_date).format("DD.MM.YYYY HH:mm");
                    }
                    if (data.form.expire_date) {
                        survey.form.expire_date = moment(data.form.expire_date).format("DD.MM.YYYY HH:mm");
                    }

                    // Populate form translations
                    tenant.value.languages.forEach((lang) => {
                        if (data.form.translations && data.form.translations[lang.code]) {
                            survey.form.translations[lang.code] = data.form.translations[lang.code];
                        }
                    });

                    // Populate fields
                    if (data.form.fields) {
                        survey.form.fields = data.form.fields.map(field => ({
                            ...field,
                            tempId: field.id || Date.now() + Math.random(),
                            options: field.options ? field.options.map(option => ({
                                ...option,
                                tempId: option.id || Date.now() + Math.random(),
                            })) : []
                        }));
                    }

                    // Check if there are submissions
                    hasSubmissions.value = data.form.submission_count > 0;
                }
            } else {
                formErrors.value = [response.error || i18n.t("Kyselyn lataaminen epäonnistui")];
            }
        };

        const translate = async (fromLangCode, toLangs) => {

            translationErrors.value = [];

            if (langOk(survey.translations, fromLangCode, ["title", "description"])) {
                isTranslating.value = true;
                const errors = await translateObjectTranslations(survey.translations, fromLangCode, toLangs, ["title", "description"]);
                translationErrors.value = errors;
                forceUpdateTranslations();
                isTranslating.value = false;

                if (errors.length == 0) {
                    translationCompleted.value = true;
                }

            }
            else {
                alert("Otsikko ja teksti on pakollinen ennen käännöstä");
            }
        }

        const forceUpdateTranslations = () => {
            forceUpdateTranslationsKey.value = new Date().toString();
        }

        onMounted(() => {
            if (props.id) {
                fetchSurvey(props.id);
            } else {
                // Set default dates for new survey
                const now = new Date();
                survey.form.release_date = `${now.getDate()}.${now.getMonth() + 1}.${now.getFullYear()} ${now.getHours()}:${now.getMinutes()}`;
            }
        });

        return {
            user,
            tenant,
            survey,
            tab,
            form,
            formErrors,
            saving,
            hasSubmissions,
            isFieldBeingEdited,
            editorOption,
            langOk,
            hasTranslation,
            updateForm,
            handleEditingStateChange,
            submit,
            translate,
            deleteSurvey,
            required,
            isTranslating,
            translationErrors,
            translationCompleted,
            forceUpdateTranslations,
            forceUpdateTranslationsKey,
            canEdit,
            icons: {
                mdiCheck,
                mdiBlockHelper,
                mdiInformation,
            },
        };
    },
};
</script>

<style>
.ql-editor {
    min-height: 200px;
}

.ql-editor p {
    font-size: 1.2em;
    line-height: 1.8em;
}
</style>
